// SPDX-License-Identifier: GPL-3.0-or-later
// Code-built light insert and bulb. The upstream light extrudes the insert outline and uses an
// emissive map. This one extrudes the outline too, adds a white outline line and a flat label
// plane. The origin is the centre of the insert on the playfield.

import { BufferGeometry, Float32BufferAttribute, PlaneGeometry, SphereGeometry } from "three";
import type { LightSpec } from "../../shared/contracts.ts";
import { INSERT_LIFT } from "../coords.ts";
import { circleOutline, extrudeOutline, type Point2, smoothLoop } from "./shared.ts";

/** Thickness of the insert plate. */
const INSERT_THICKNESS = 0.6;
/**
 * The insert is as wide as the glow radius (`falloff`). A larger insert gives a larger label.
 * The inserts of the table are at least 47 units apart, so they do not touch.
 */
const INSERT_SCALE = 1.0;
/** Default glow radius. */
const FALLOFF_DEFAULT = 25;
/** A label with more than this many letters on its longest line gets a wider insert. */
const STRETCH_FROM = 3;
/** Each extra letter makes the insert this much wider (in insert radii). */
const STRETCH_PER_LETTER = 0.25;
/** The widest insert, in insert radii. */
const STRETCH_MAX = 2;

/** The parts of a light. */
export interface LightGeometry {
  /** The insert plate. */
  readonly fill: BufferGeometry;
  /** The outline as line positions (closed loop). */
  readonly outline: BufferGeometry;
  /** A flat plane for the label, as wide as the insert. Null when the light has no label. */
  readonly label: BufferGeometry | null;
  /** Width over height of the label plane. The label texture has the same ratio. */
  readonly labelAspect: number;
  /** A sphere for a bulb. Null when the light has no bulb mesh. */
  readonly bulb: BufferGeometry | null;
  /** Radius of the insert. */
  readonly radius: number;
  /** Height of the bulb centre above the playfield. */
  readonly bulbHeight: number;
}

/** Number of letters on the longest line of a label. Lines break at spaces. */
function longestLine(label: string | undefined): number {
  return (label ?? "").split(" ").reduce((max, word) => Math.max(max, word.length), 0);
}

/**
 * How many insert radii wide a round insert is. A short label sits in a circle (1). A long
 * word needs a wider insert, or its letters would be too small to read.
 */
export function insertStretch(spec: LightSpec): number {
  const extra = longestLine(spec.label) - STRETCH_FROM;
  if (extra <= 0) return 1;
  return Math.min(STRETCH_MAX, 1 + extra * STRETCH_PER_LETTER);
}

/** Points of the insert outline, relative to the centre of the light. */
export function insertOutline(spec: LightSpec): Point2[] {
  const radius = (spec.falloff ?? FALLOFF_DEFAULT) * INSERT_SCALE;
  if (spec.dragPoints && spec.dragPoints.length >= 3) {
    const points = smoothLoop(spec.dragPoints).map((p) => ({
      x: p.x - spec.center.x,
      y: p.y - spec.center.y,
    }));
    if (points.length >= 3) return points;
  }
  const stretch = insertStretch(spec);
  return circleOutline(0, 0, radius, 28).map((p) => ({
    x: p.x * stretch,
    y: p.y,
  }));
}

/** Radius that covers the insert outline. */
function outlineRadius(points: readonly Point2[]): number {
  let max = 0;
  for (const p of points) max = Math.max(max, Math.hypot(p.x, p.y));
  return max;
}

/** True when the light has a bulb mesh. */
export function hasBulb(spec: LightSpec): boolean {
  return spec.bulbLight === true || spec.showBulbMesh === true;
}

/** Build the geometry of a light. */
export function generateLight(spec: LightSpec): LightGeometry {
  const points = insertOutline(spec);
  const radius = outlineRadius(points);

  const fill = extrudeOutline(points, INSERT_THICKNESS);
  fill.translate(0, INSERT_LIFT, 0);
  fill.name = `light.fill-${spec.name}`;

  const lineY = INSERT_LIFT + INSERT_THICKNESS + 0.1;
  const positions: number[] = [];
  for (const p of points) positions.push(p.x, lineY, p.y);
  const outline = new BufferGeometry();
  outline.setAttribute("position", new Float32BufferAttribute(positions, 3));
  outline.name = `light.outline-${spec.name}`;

  // The label plane is the bounding box of the insert. The label canvas draws its text inside
  // the ellipse of that box, so the text stays inside the insert.
  let halfX = 0;
  let halfY = 0;
  for (const p of points) {
    halfX = Math.max(halfX, Math.abs(p.x));
    halfY = Math.max(halfY, Math.abs(p.y));
  }
  const labelAspect = halfY > 0 ? halfX / halfY : 1;
  let label: BufferGeometry | null = null;
  if (spec.label !== undefined && spec.label !== "") {
    label = new PlaneGeometry(halfX * 2, halfY * 2);
    label.rotateX(-Math.PI / 2);
    label.translate(0, lineY + 0.1, 0);
    label.name = `light.label-${spec.name}`;
  }

  let bulb: BufferGeometry | null = null;
  const bulbHeight = spec.bulbHaloHeight ?? 12;
  if (hasBulb(spec)) {
    bulb = new SphereGeometry(spec.meshRadius ?? 6, 16, 12);
    bulb.translate(0, bulbHeight, 0);
    bulb.name = `light.bulb-${spec.name}`;
  }
  return { fill, outline, label, labelAspect, bulb, radius, bulbHeight };
}
